/**
 * Section statistics document schemas and Firebase paths.
 *
 * Defines the Firestore schema for section speaking statistics and percentile
 * tracking. Shared between the webapp and functions packages.
 */

import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import {SharedZoomSpeakingStatsFieldsSchema} from '../teacherFeedback/zoomSpeakingStatsShared';
import {
  parseSectionId,
  parseSectionWeekId,
  type SectionId,
  type SectionWeekId,
} from '../../../section/sectionApi';

// ============================================================================
// Section Statistics Document Schema
// ============================================================================

/**
 * Schema for a section statistics Firestore document.
 *
 * Uses `.loose()` to allow additional Firestore metadata fields.
 * For compile-time branded type safety, use the
 * {@link SectionStatisticsDocument} interface.
 */
export const SectionStatisticsDocumentSchema = z
  .object({
    sectionId: z
      .string()
      .refine(s => parseSectionId(s) != null, {message: 'Invalid sectionId'})
      .transform((s): SectionId => {
        const parsed = parseSectionId(s);
        if (parsed == null) throw new Error('Invalid sectionId');
        return parsed;
      }),
    sectionWeekId: z
      .number()
      .refine(n => parseSectionWeekId(n) != null, {
        message: 'Invalid sectionWeekId',
      })
      .transform((n): SectionWeekId => {
        const parsed = parseSectionWeekId(n);
        if (parsed == null) throw new Error('Invalid sectionWeekId');
        return parsed;
      }),
    courseId: z.string(),
  })
  .extend(SharedZoomSpeakingStatsFieldsSchema.shape)
  .extend({
    studentTalkPercentile: z.number().optional(),
    slTalkPercentile: z.number().optional(),
    breakoutRoomPercentile: z.number().optional(),
    percentileCalculatedAt: FirestoreTimestampSchema.optional(),
  })
  .loose();

/** A time interval with a start and end in epoch milliseconds. */
export interface TimeInterval {
  /** The start time of the interval in seconds relative to recording start. */
  start: number;
  /** The end time of the interval in seconds relative to recording start. */
  end: number;
}

/**
 * Document storing speaking statistics for a section's week.
 * Stored at: `course/{courseId}/sectionWeekStats/{sectionId}_week_{weekId}`
 */
export type SectionStatisticsDocument = z.infer<
  typeof SectionStatisticsDocumentSchema
>;

// ============================================================================
// Callable Function Definitions
// ============================================================================

/**
 * Schema for a request to calculate section statistics.
 *
 * For compile-time branded type safety, use the
 * {@link CalculateSectionStatisticsRequest} interface.
 */
export const CalculateSectionStatisticsRequestSchema = z.object({
  courseId: z.string(),
  sectionId: z.string(),
  sectionWeekId: z.number().int().min(1),
});

/** Request to calculate section statistics.
 *
 * Uses branded types (not z.infer) for compile-time type safety.
 * For runtime validation, use {@link CalculateSectionStatisticsRequestSchema}.
 */
export interface CalculateSectionStatisticsRequest {
  /** The course ID for the section. */
  courseId: string;
  /** The section ID to calculate statistics for. */
  sectionId: SectionId;
  /** The week number of the section. */
  sectionWeekId: SectionWeekId;
}

/** Schema for the response from calculate section statistics function. */
export const CalculateSectionStatisticsResponseSchema = z.object({
  success: z.boolean(),
  stats: SectionStatisticsDocumentSchema.optional(),
  error: z.string().optional(),
});

/** Response from calculate section statistics function.
 *
 * Uses branded types (not z.infer) for compile-time type safety.
 * For runtime validation, use {@link CalculateSectionStatisticsResponseSchema}.
 */
export interface CalculateSectionStatisticsResponse {
  /** Whether the calculation was successful. */
  success: boolean;
  /** The calculated statistics document. */
  stats?: SectionStatisticsDocument;
  /** Error message if calculation failed. */
  error?: string;
}

/**
 * Schema for a request to calculate percentiles for all sections in a course/week.
 *
 * For compile-time branded type safety, use the
 * {@link CalculateSectionPercentileRequest} interface.
 */
export const CalculateSectionPercentileRequestSchema = z.object({
  courseId: z.string(),
  sectionWeekId: z.number().int().min(1),
});

/** Request to calculate percentiles for all sections in a course/week.
 *
 * Uses branded types (not z.infer) for compile-time type safety.
 * For runtime validation, use {@link CalculateSectionPercentileRequestSchema}.
 */
export interface CalculateSectionPercentileRequest {
  /** The course ID. */
  courseId: string;
  /** The week number to calculate percentiles for. */
  sectionWeekId: SectionWeekId;
}

/** Schema for the response from calculate section percentiles function. */
export const CalculateSectionPercentileResponseSchema = z.object({
  success: z.boolean(),
  sectionsUpdated: z.number().optional(),
  error: z.string().optional(),
});

/** Response from calculate section percentiles function. */
export type CalculateSectionPercentileResponse = z.infer<
  typeof CalculateSectionPercentileResponseSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to the section week stats collection for a course.
 * Collection path: `course/{courseId}/sectionWeekStats`
 */
export function getSectionWeekStatsCollectionPath(courseId: string): string {
  return `course/${courseId}/sectionWeekStats`;
}

/**
 * Constructs the document ID for a section week stats document.
 * Document ID format: `{sectionId}_week_{weekId}`
 */
export function getSectionWeekStatsDocumentId({
  sectionId,
  sectionWeekId,
}: {
  sectionId: SectionId;
  sectionWeekId: SectionWeekId;
}): string {
  return `${sectionId}_week_${String(sectionWeekId)}`;
}

/**
 * Constructs the path to a specific section week stats document.
 * Document path: `course/{courseId}/sectionWeekStats/{sectionId}_week_{weekId}`
 */
export function getSectionWeekStatsDocumentPath({
  courseId,
  sectionId,
  sectionWeekId,
}: {
  courseId: string;
  sectionId: SectionId;
  sectionWeekId: SectionWeekId;
}): string {
  const collectionPath = getSectionWeekStatsCollectionPath(courseId);
  const documentId = getSectionWeekStatsDocumentId({sectionId, sectionWeekId});
  return `${collectionPath}/${documentId}`;
}
